import { useState, type FC } from 'react'
import { observer } from 'mobx-react-lite'
import { Tree } from '@minoru/react-dnd-treeview'
import { MenuTitle, EmptyState } from 'shared/ui'
import {
  type IDropOptions,
  type IInboxTreeCombine,
  type INodeModel,
  TreeRootInboxes,
} from 'entities/Inbox'
import { useEntitiesContext } from 'entities/EntitiesExport'
import { MenuCardTree } from '../MenuCardTree/MenuCardTree'
import { MenuPlaceholderTree } from '../MenuPlaceholderTree/MenuPlaceholderTree'
import { MenuCardPreviewTree } from '../MenuCardPreviewTree/MenuCardPreviewTree'
import styles from './styles.module.scss'

type IMenuSectionTreeProps = {
  title: string
  items: INodeModel<IInboxTreeCombine>[]
  count?: number | undefined
  root: TreeRootInboxes
  handleDrop: (
    items: INodeModel<IInboxTreeCombine>[],
    options: IDropOptions<IInboxTreeCombine>,
    root: TreeRootInboxes
  ) => void
  useSearch?: {
    handleSearch: (search: string) => void
    search: string
    placeholder?: string
    autofocus?: boolean
    variant?: 'simple' | 'transparent'
  }
  onClickItemTree?: () => void
}

export const MenuSectionTree: FC<IMenuSectionTreeProps> = observer((props) => {
  const { title = 'Test', items = [], count, root, handleDrop, useSearch, onClickItemTree } = props
  const [openCollapse, setOpenCollapse] = useState(true)
  const { inboxesStore } = useEntitiesContext()
  const {
    selectCurrentInboxId,
    isChildOfGroupInboxWithActiveChild,
    isChildOfActiveGroupInbox,
    hasActiveChildInbox,
  } = inboxesStore
  const { localInboxes } = inboxesStore
  const isEmpty = !items.length
  const showList = !isEmpty && (openCollapse || useSearch?.search)
  const currentInbox = localInboxes.currentSmartViewInboxId || selectCurrentInboxId

  return (
    <div className={styles.wrap}>
      <MenuTitle
        useSearch={useSearch}
        title={title}
        count={count}
        open={openCollapse}
        handleToggle={() => setOpenCollapse(!openCollapse)}
      />

      {isEmpty && (openCollapse || useSearch?.search) && (
        <div className={styles.empty}>
          <EmptyState
            noIcon
            alignItems={'flex-start'}
            typographyProps={{
              variant: 'body-md-regular',
              color: 'var(--content-primary-disabled)',
              ariaLabel: 'search',
            }}
          />
        </div>
      )}

      <Tree
        tree={items}
        rootId={root}
        render={(node, { isOpen, onToggle, hasChild }) => {
          const inboxId = node.data?.id
          const activeInbox =
            inboxId === currentInbox ||
            hasActiveChildInbox(inboxId) ||
            isChildOfActiveGroupInbox(inboxId) ||
            isChildOfGroupInboxWithActiveChild(inboxId)

          if (!showList && activeInbox) {
            return (
              <MenuCardTree
                rootId={root}
                node={node}
                onToggle={onToggle}
                isOpen={isOpen}
                hasChild={hasChild}
                onClickItemTree={onClickItemTree}
              />
            )
          }

          if (!showList) {
            return <></>
          }

          return (
            <MenuCardTree
              rootId={root}
              node={node}
              onToggle={onToggle}
              isOpen={isOpen}
              hasChild={hasChild}
              onClickItemTree={onClickItemTree}
            />
          )
        }}
        dragPreviewRender={(monitorProps) => {
          return <MenuCardPreviewTree node={monitorProps.item} />
        }}
        onDrop={(tree, options) => handleDrop(tree, options, root)}
        classes={{
          root: styles.treeRoot,
          draggingSource: styles.draggingSource,
          placeholder: styles.placeholderContainer,
          dropTarget: styles.dropTarget,
        }}
        sort={false}
        insertDroppableFirst={false}
        canDrop={(tree, { dragSource, dropTargetId, dropTarget }) => {
          if (dragSource?.data?.isSmartViewInbox) {
            if (dropTarget?.data?.isGroupInbox) return false
            if (dropTargetId === TreeRootInboxes.SIMPLE) return false
          }

          if (dragSource?.data?.isGroupInbox) {
            if (dropTarget?.data?.isGroupInbox) return false
          }

          if (dragSource?.data?.isUnifiedInbox) {
            if (dropTarget?.data?.isGroupInbox) return false
          }

          if (dragSource?.parent === dropTargetId) return true
        }}
        dropTargetOffset={10}
        placeholderRender={(node, { depth }) => <MenuPlaceholderTree node={node} depth={depth} />}
      />
    </div>
  )
})
